import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { TIERS } from "../lib/i18n";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Plus, Users, Lock, Clock } from "lucide-react";

export default function Pools() {
  const { t, lang } = useLang();
  const { fmt } = useCurrency();
  const { user } = useAuth();
  const [pools, setPools] = useState([]);

  useEffect(() => { api.get("/pools").then((r) => setPools(r.data)); }, []);

  const userMax = user?.tier_max_pool || 20;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient">{t("pools")}</h1>
          <p className="text-xs text-[#8FA3BD] mt-1">{t("maxPool")}: {userMax === 999999 ? "∞" : `$${userMax}`}</p>
        </div>
        {user?.role === "admin" && (
          <Link to="/pools/new"><Button className="btn-cyan" data-testid="create-pool-page-btn"><Plus className="w-4 h-4 me-1.5" /> {t("createPool")}</Button></Link>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {pools.map((p) => {
          const tier = p.tier || "bronze";
          const tierInfo = TIERS[tier] || TIERS.admin;
          const locked = !p.is_admin_pool && p.pot_per_round > userMax;
          return (
            <Link key={p.pool_id} to={`/pools/${p.pool_id}`}>
              <div className={`bento-card p-6 h-full relative ${locked ? "opacity-60" : ""}`} data-testid={`pool-card-${p.pool_id}`}>
                {locked && (
                  <div className="absolute top-3 end-3 text-[#D4AF37]"><Lock className="w-4 h-4" /></div>
                )}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-lg">{p.name}</div>
                    <div className="text-xs text-[#8FA3BD] mt-1">{p.description || "—"}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full ${tierInfo.cls}`}>
                    {lang === "ar" ? tierInfo.label_ar : tierInfo.label_en}
                  </span>
                </div>
                <div className="text-3xl font-bold text-cyan-gradient">{fmt(p.pot_per_round)}</div>
                <div className="text-xs text-[#8FA3BD] mb-4">{fmt(p.amount_per_round)} × {p.member_count} • {t(p.frequency || "daily")}</div>
                <div className="flex items-center justify-between text-xs text-[#8FA3BD]">
                  <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {p.members.length} / {p.member_count}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "open" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : p.status === "active" ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#1A2D4D] text-[#8FA3BD]"}`}>{t(p.status)}</span>
                </div>
                <div className="mt-3 h-1.5 bg-[#1A2D4D] rounded-full overflow-hidden">
                  <div className="h-full btn-cyan" style={{ width: `${(p.members.length / p.member_count) * 100}%` }} />
                </div>
                {p.status === "completed" && (
                  <div className="text-xs text-[#D4AF37] mt-3 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {t("poolEnded")}</div>
                )}
                {locked && (
                  <div className="text-xs text-[#D4AF37] mt-3">{t("lockedTier")}</div>
                )}
              </div>
            </Link>
          );
        })}
        {pools.length === 0 && <div className="col-span-full text-center text-[#8FA3BD] py-12">{t("noPools")}</div>}
        {/* Coming Soon higher-tier pools */}
        {[100, 200, 500, 1000].map((amt) => (
          <div key={amt} className="bento-card p-5 relative opacity-50 cursor-not-allowed" data-testid={`pool-soon-${amt}`}>
            <div className="absolute top-3 end-3 bg-[#FF9500]/20 text-[#FF9500] text-[10px] px-2 py-1 rounded-full font-bold">قريباً</div>
            <div className="text-lg font-bold">سلفة ${amt} اليومية</div>
            <div className="text-xs text-[#8FA3BD] mt-1">10 أعضاء · مكاسب ${amt * 10}</div>
            <div className="text-3xl mt-4 text-[#D4AF37]">🔒</div>
            <div className="text-xs text-[#FF9500] mt-2">متاحة قريباً</div>
          </div>
        ))}
      </div>
    </div>
  );
}
